import { useEffect, useState } from 'react';
import LocalStorage from './LocalStorage';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'theme-preference';

export function getThemePreference(): ThemePreference {
  try {
    const v = LocalStorage.get(LocalStorage.Keys.ThemePreference);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // ignore — privacy mode, etc.
  }
  return 'system';
}

export function setThemePreference(value: ThemePreference): void {
  try {
    if (value === 'system') {
      LocalStorage.remove(LocalStorage.Keys.ThemePreference);
    } else {
      LocalStorage.set(LocalStorage.Keys.ThemePreference, value);
    }
  } catch {
    // ignore
  }
  applyThemePreference(value);
  window.dispatchEvent(new CustomEvent('theme-preference-change', { detail: value }));
}

export function applyThemePreference(value: ThemePreference): void {
  if (value === 'system') {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = value;
  }
}

export function useThemePreference(): [ThemePreference, (v: ThemePreference) => void] {
  const [pref, setPrefState] = useState<ThemePreference>(getThemePreference);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) {
        setPrefState(getThemePreference());
      }
    };
    const onLocal = (e: Event) => {
      const detail = (e as CustomEvent<ThemePreference>).detail;
      if (detail === 'light' || detail === 'dark' || detail === 'system') {
        setPrefState(detail);
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('theme-preference-change', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('theme-preference-change', onLocal);
    };
  }, []);

  const set = (v: ThemePreference): void => {
    setThemePreference(v);
    setPrefState(v);
  };

  return [pref, set];
}
